"use client";

import {
    getAdminCourseApiV1AdminCoursesCourseIdGet,
    getAdminCourseMetadataApiV1AdminCoursesCourseIdMetadataGet,
} from "@/src/api/sdk.gen";
import { useEffect, useState } from "react";
import {
    type CanonicalApiResponse,
    type MetadataApiResponse,
    type CanonicalDraftHydrationData,
    mapCanonicalDraftHydrationData,
} from "./hydrationMapper";

type UseCanonicalDraftHydrationArgs = {
    editingCourseId: number | null;
};

export function useCanonicalDraftHydration({ editingCourseId }: UseCanonicalDraftHydrationArgs) {
    const [isHydrating, setIsHydrating] = useState(false);
    const [hydrationError, setHydrationError] = useState<string | null>(null);
    const [hydrationData, setHydrationData] = useState<CanonicalDraftHydrationData | null>(null);

    useEffect(() => {
        if (!editingCourseId) return;
        let mounted = true;

        setIsHydrating(true);
        setHydrationError(null);
        setHydrationData(null);

        (async () => {
            try {
                const [courseRes, metadataRes] = await Promise.all([
                    getAdminCourseApiV1AdminCoursesCourseIdGet({ path: { course_id: editingCourseId } }),
                    getAdminCourseMetadataApiV1AdminCoursesCourseIdMetadataGet({ path: { course_id: editingCourseId } }),
                ]);

                if (courseRes.error || !courseRes.data) {
                    const data = (courseRes.error || {}) as { error?: string };
                    throw new Error(data.error || "Failed to load course draft");
                }
                if (metadataRes.error || !metadataRes.data) {
                    const data = (metadataRes.error || {}) as { error?: string };
                    throw new Error(data.error || "Failed to load course metadata");
                }

                const course = courseRes.data as unknown as CanonicalApiResponse;
                const metadata = metadataRes.data as unknown as MetadataApiResponse;
                if (!mounted) return;

                setHydrationData(mapCanonicalDraftHydrationData(course, metadata));
            } catch (e) {
                if (!mounted) return;
                setHydrationError(e instanceof Error ? e.message : "Failed to load draft");
            } finally {
                if (mounted) setIsHydrating(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [editingCourseId]);

    return { isHydrating, hydrationError, hydrationData };
}

